import { supabase } from '../lib/supabase';

/**
 * Service for managing friend duel activity feed
 * Provides real-time updates for friend duels, proof uploads, and verification progress
 */
export const friendDuelActivityService = {
  /**
   * Get all friend duels with activity (challenges, proofs, verifications)
   */
  async getFriendDuelActivity(userId, limit = 20) {
    try {
      // Get user's friends
      const { data: friendships, error: friendError } = await supabase
        ?.from('friendships')?.select('friend_id')?.eq('user_id', userId)?.eq('status', 'accepted');

      if (friendError) throw friendError;

      const friendIds = friendships?.map(f => f?.friend_id) || [];
      if (friendIds?.length === 0) return [];

      // Get all duels involving friends (as creator or opponent)
      const { data: duels, error: duelsError } = await supabase
        ?.from('duels')
        ?.select(`
          *,
          creator:user_profiles!duels_creator_id_fkey(id, full_name, avatar_url),
          opponent:user_profiles!duels_opponent_id_fkey(id, full_name, avatar_url),
          proofs:duel_proofs(
            id,
            photo_url,
            auto_verified,
            created_at,
            user_id,
            user:user_profiles(id, full_name, avatar_url)
          )
        `)
        ?.or(`creator_id.in.(${friendIds?.join(',')}),opponent_id.in.(${friendIds?.join(',')})`)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (duelsError) throw duelsError;

      // Transform data to include activity metadata
      const activityFeed = duels?.map(duel => {
        const latestProof = duel?.proofs?.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )?.[0];

        const totalProofs = duel?.proofs?.length || 0;
        const verifiedProofs = duel?.proofs?.filter(p => p?.auto_verified)?.length || 0;
        const verificationProgress = totalProofs > 0 ? (verifiedProofs / totalProofs) * 100 : 0;

        return {
          ...duel,
          activity: {
            latestProof,
            totalProofs,
            verifiedProofs,
            verificationProgress,
            hasNewActivity: latestProof && 
              new Date(latestProof.created_at) > new Date(Date.now() - 60000), // Last minute
            canJoin: duel?.status === 'pending' && duel?.opponent_id === null
          }
        };
      });

      return activityFeed;
    } catch (error) {
      console.error('Error fetching friend duel activity:', error);
      throw error;
    }
  },

  /**
   * Get unread activity count for notification badge
   */
  async getUnreadActivityCount(userId, lastViewedAt) {
    try {
      // Get user's friends
      const { data: friendships, error: friendError } = await supabase
        ?.from('friendships')
        ?.select('friend_id')
        ?.eq('user_id', userId)
        ?.eq('status', 'accepted');

      if (friendError) throw friendError;

      const friendIds = friendships?.map(f => f?.friend_id) || [];
      if (friendIds?.length === 0) return 0;

      // Count duels created after lastViewedAt
      const { count: newDuelsCount, error: duelsError } = await supabase
        ?.from('duels')
        ?.select('id', { count: 'exact', head: true })
        ?.or(`creator_id.in.(${friendIds?.join(',')}),opponent_id.in.(${friendIds?.join(',')})`)
        ?.gt('created_at', lastViewedAt);

      if (duelsError) throw duelsError;

      // Count proofs uploaded after lastViewedAt
      const { count: newProofsCount, error: proofsError } = await supabase
        ?.from('duel_proofs')
        ?.select('id', { count: 'exact', head: true })
        ?.in('user_id', friendIds)
        ?.gt('created_at', lastViewedAt);

      if (proofsError) throw proofsError;

      return (newDuelsCount || 0) + (newProofsCount || 0);
    } catch (error) {
      console.error('Error fetching unread activity count:', error);
      return 0;
    }
  },

  /**
   * Join an open duel
   */
  async joinDuel(duelId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('duels')
        ?.update({ 
          opponent_id: userId,
          status: 'active',
          start_time: new Date()?.toISOString()
        })
        ?.eq('id', duelId)
        ?.eq('status', 'pending')
        ?.is('opponent_id', null)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error joining duel:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time friend duel activity
   */
  subscribeFriendDuelActivity(userId, friendIds, callbacks) {
    const { onDuelCreated, onProofUploaded, onVerificationComplete } = callbacks || {};

    if (!friendIds || friendIds?.length === 0) {
      return { unsubscribe: () => {} };
    }

    // Subscribe to new duels from friends
    const duelsChannel = supabase
      ?.channel('friend_duels_activity')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duels'
        },
        async (payload) => {
          // Check if duel involves a friend
          if (friendIds?.includes(payload?.new?.creator_id) || 
              friendIds?.includes(payload?.new?.opponent_id)) {
            onDuelCreated?.(payload);
          }
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'duels'
        },
        async (payload) => {
          // Check if duel involves a friend
          if (friendIds?.includes(payload?.new?.creator_id) || 
              friendIds?.includes(payload?.new?.opponent_id)) {
            onDuelCreated?.(payload);
          }
        }
      )
      ?.subscribe();

    // Subscribe to proof uploads from friends
    const proofsChannel = supabase
      ?.channel('friend_proofs_activity')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'duel_proofs'
        },
        async (payload) => {
          // Check if proof is from a friend
          if (friendIds?.includes(payload?.new?.user_id)) {
            onProofUploaded?.(payload);
          }
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'duel_proofs'
        },
        async (payload) => {
          // Check if verification status changed
          if (friendIds?.includes(payload?.new?.user_id) && 
              payload?.new?.auto_verified !== payload?.old?.auto_verified) {
            onVerificationComplete?.(payload);
          }
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        duelsChannel?.unsubscribe();
        proofsChannel?.unsubscribe();
      }
    };
  }
};